import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { Box } from '@/components/Ad/Promotions/PromotionFlow.styles';
import { getCardSystemLogo } from '@/components/Payment/PaymentMethods.helpers';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import * as S from '@/components/Payment/PaymentMethods.styles';
import { PaymentMethodOption } from '@/components/Payment/PaymentMethods/PaymentMethodOption';
import { SavedCardOption } from '@/components/Payment/PaymentMethods/SavedCardOption';
import { usePaymentOptions } from '@/components/Payment/PaymentMethods/usePaymentOptions';
import { PaymentMethod } from '@/api/types/payments.types';
import { GetUserResponse, UserCard } from '@/api/types/user.types';
import { GET_USER } from '@/api/queries/user.queries';
import { useAuthStore } from '@/store/useAuthStore';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useAdDetails } from '@/hooks/useAdDetails';
import { useMobileDetect } from '@/hooks/useMobileDetect';
import { useServerDataContext } from '@/contexts';
import { ModalSheet } from '@/components/UI/ModalSheet';

type Props = {
  payment: PaymentMethod;
  setPayment: React.Dispatch<React.SetStateAction<PaymentMethod>>;
  setCardId?: React.Dispatch<React.SetStateAction<string | undefined>>;
  cardId?: string;
  isAddBalanceOpen?: boolean;
  isPromotionFlowOpen?: boolean;
  isAppleGooglePayEnabled?: boolean;
  paymentMethods?: PaymentMethod[];
};

function getDefaultCardPayment(isB2B?: boolean, hasSavedCards?: boolean) {
  return isB2B || !hasSavedCards ? PaymentMethod.Card : PaymentMethod.My_card;
}

export const PaymentMethods = ({
  payment,
  setPayment,
  setCardId,
  cardId,
  isAddBalanceOpen,
  isPromotionFlowOpen,
  isAppleGooglePayEnabled: isAppleGooglePayEnabledProp,
  paymentMethods: paymentMethodsProp,
}: Props) => {
  const { t } = useTranslation();
  const [isCardsModalOpen, toggleCardsModal] = useToggleOpenState();
  const [isAuthenticated] = useAuthStore(state => [state.isAuthenticated]);
  const { isWebView } = useBridgeState();
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const paymentMethods = paymentMethodsProp ?? adData?.paymentMethods;
  const isAppleGooglePayEnabled =
    isAppleGooglePayEnabledProp ?? adData?.isAppleGooglePayEnabled;
  const { isAndroid, isIos } = useMobileDetect();

  const { data: userInfo } = useQuery<GetUserResponse>(GET_USER);

  const savedCards = userInfo?.currentUser?.cards?.nodes;
  const walletBalance = userInfo?.currentUser?.walletBalance;
  const hasSavedCards = !!savedCards?.length;

  const selectedCard = savedCards?.find(card => card.id === cardId) || null;

  const isB2B = useMemo(
    () => isAuthenticated && userInfo?.currentUser?.isShop,
    [isAuthenticated, userInfo]
  );

  const paymentOptions = usePaymentOptions({
    t,
    paymentMethods,
    walletBalance,
    isAddBalanceOpen,
    isAuthenticated,
    isWebView,
    isB2B,
    isAppleGooglePayEnabled,
    isIos,
    isAndroid,
  });

  function handleMyCardClick(card: UserCard) {
    setCardId?.(card.id);
    toggleCardsModal(false);
  }

  function handleMyCardsClick(e: React.MouseEvent) {
    e.stopPropagation();
    toggleCardsModal();
  }

  useEffect(() => {
    if (isAddBalanceOpen) {
      setPayment(getDefaultCardPayment(isB2B, hasSavedCards));
    }
  }, [hasSavedCards, isAddBalanceOpen, isB2B, setPayment]);

  useEffect(() => {
    if (isPromotionFlowOpen) {
      if (isB2B) {
        setPayment(PaymentMethod.Wallet);
      } else {
        setPayment(hasSavedCards ? PaymentMethod.My_card : PaymentMethod.Card);
      }
    }
  }, [isPromotionFlowOpen]);

  useEffect(() => {
    if (!hasSavedCards) {
      setCardId?.(undefined);
      return;
    }

    const selectedSavedCard = savedCards.find(card => card.id === cardId);
    const nextSelectedCard = selectedSavedCard ?? savedCards[0];

    setCardId?.(nextSelectedCard.id);
  }, [hasSavedCards, savedCards, cardId, setCardId]);

  return (
    <>
      <Box>
        <S.DrawerItemTitle>{t('pages.ad.payment_method')}</S.DrawerItemTitle>

        <S.List>
          {selectedCard && (
            <SavedCardOption
              card={selectedCard}
              isSelected={payment === PaymentMethod.My_card}
              cardsButtonLabel={t('pages.cabinet.my_cards')}
              onSelect={() => setPayment(PaymentMethod.My_card)}
              onOpenCards={handleMyCardsClick}
            />
          )}

          {paymentOptions.map(option => (
            <PaymentMethodOption
              key={option.value}
              option={option}
              isSelected={option.value === payment}
              onSelect={setPayment}
            />
          ))}
        </S.List>
      </Box>

      <ModalSheet
        dataTestId={AdPageTestIds.myCards.window.self}
        isOpen={isCardsModalOpen}
        onClose={() => toggleCardsModal(false)}
        title={t('pages.cabinet.my_cards')}
      >
        {userInfo?.currentUser?.cards?.nodes.map(card => (
          <S.CardBox
            data-testid={AdPageTestIds.myCards.window.card}
            key={card.id}
            $isActive={card.id === cardId}
            onClick={() => handleMyCardClick(card)}
          >
            <S.CardName $variant="1">{card.name}</S.CardName>

            <IconBox
              $filename={`${getCardSystemLogo(card)}.png`}
              $width={45}
              $height={30}
              $iconWidth={34}
            />
          </S.CardBox>
        ))}
      </ModalSheet>
    </>
  );
};
